import { DatePipe } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { FinanceApiService, FinancialBook } from '../finance/finance-api.service';
import { SalesApiService, SalesDocumentProfileRecord } from '../sales/sales-api.service';
import {
  PlatformAdminService,
  RunningNumberDefinitionRecord,
  RunningNumberHistoryRecord,
  SaveRunningNumberDefinitionRequest
} from './platform-admin.service';

@Component({
  standalone: true,
  selector: 'ain-platform-running-numbers',
  imports: [AinPageComponent, FormsModule, TableModule, ButtonModule, InputTextModule, SelectModule, DialogModule, DatePipe],
  template: `
    <ain-page [eyebrow]="t('platform.eyebrow')" [title]="t('platform.runningNumbers.title')" [description]="t('platform.runningNumbers.description')">
      <div class="toolbar">
        <p-select
          class="entity-filter"
          [options]="entityTypeFilterOptions()"
          optionLabel="label"
          optionValue="value"
          [filter]="true"
          filterBy="label,value"
          [(ngModel)]="filterEntityType"
          (ngModelChange)="load()"
          appendTo="body">
        </p-select>
        <button pButton type="button" [label]="t('platform.runningNumbers.refresh')" severity="secondary" (click)="load()"></button>
        <button pButton type="button" [label]="t('common.create')" (click)="openCreateDialog()"></button>
      </div>

      <p-table [value]="rows()">
        <ng-template pTemplate="header">
          <tr>
            <th>{{ t('platform.runningNumbers.fields.entityType') }}</th>
            <th>{{ t('platform.runningNumbers.fields.preview') }}</th>
            <th>{{ t('platform.runningNumbers.fields.currentNumber') }}</th>
            <th>{{ t('platform.runningNumbers.fields.strategy') }}</th>
            <th>{{ t('columns.status') }}</th>
            <th>{{ t('platform.runningNumbers.fields.actions') }}</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-row>
          <tr>
            <td>{{ entityTypeLabel(row.entityType) }}</td>
            <td>{{ row.preview }}</td>
            <td>{{ row.currentNumber }}</td>
            <td>{{ strategyLabel(row.strategy) }}</td>
            <td>{{ row.isActive ? t('common.yes') : t('common.no') }}</td>
            <td class="actions-cell">
              <button pButton type="button" text="true" size="small" [label]="t('common.edit')" (click)="openEditDialog(row)"></button>
              <button pButton type="button" text="true" size="small" severity="warn" [label]="t('platform.runningNumbers.actions.resetCounter')" (click)="resetCounter(row)"></button>
              <button pButton type="button" text="true" size="small" severity="contrast" [label]="t('platform.runningNumbers.actions.setCounter')" (click)="setCounter(row)"></button>
              <button pButton type="button" text="true" size="small" severity="secondary" [label]="t('platform.runningNumbers.actions.history')" (click)="openHistory(row)"></button>
              <button pButton type="button" text="true" size="small" severity="danger" [label]="t('common.delete')" (click)="deleteDefinition(row)"></button>
            </td>
          </tr>
        </ng-template>
      </p-table>

      @if (errorMessage()) {
        <p class="message error">{{ errorMessage() }}</p>
      }
      @if (successMessage()) {
        <p class="message success">{{ successMessage() }}</p>
      }

      <p-dialog
        [header]="dialogMode() === 'create' ? t('platform.runningNumbers.form.createTitle') : t('platform.runningNumbers.form.editTitle')"
        [visible]="dialogOpen()"
        [modal]="true"
        [style]="{ width: '64rem', maxWidth: '96vw' }"
        (visibleChange)="dialogOpen.set($event)">
        <div class="form-grid">
          <label>
            <span>{{ t('platform.runningNumbers.fields.entityType') }}</span>
            <p-select
              [options]="entityTypeOptions()"
              optionLabel="label"
              optionValue="value"
              [filter]="true"
              filterBy="label,value"
              [(ngModel)]="form.entityType"
              (ngModelChange)="onEntityTypeChanged($event)"
              appendTo="body">
            </p-select>
          </label>
          <label>
            <span>{{ t('platform.runningNumbers.fields.parentEntityType') }}</span>
            <p-select
              [options]="parentEntityTypeOptions()"
              optionLabel="label"
              optionValue="value"
              [filter]="true"
              filterBy="label,value"
              [(ngModel)]="form.parentEntityType"
              (ngModelChange)="onParentEntityTypeChanged($event)"
              appendTo="body">
            </p-select>
          </label>
          <label>
            <span>{{ t('platform.runningNumbers.fields.parentEntityId') }}</span>
            @if (usesFinancialBookParent()) {
              <p-select
                [options]="bookOptions()"
                optionLabel="label"
                optionValue="value"
                [filter]="true"
                filterBy="label,value"
                [placeholder]="t('platform.runningNumbers.fields.selectBook')"
                [(ngModel)]="form.parentEntityId"
                (ngModelChange)="refreshPreview()"
                appendTo="body">
              </p-select>
            } @else if (usesDocumentProfileParent()) {
              <p-select
                [options]="documentProfileOptions()"
                optionLabel="label"
                optionValue="value"
                [filter]="true"
                filterBy="label,value"
                [placeholder]="t('platform.runningNumbers.fields.selectDocumentProfile')"
                [(ngModel)]="form.parentEntityId"
                (ngModelChange)="refreshPreview()"
                appendTo="body">
              </p-select>
            } @else {
              <input pInputText type="text" [(ngModel)]="form.parentEntityId" (ngModelChange)="refreshPreview()" />
            }
          </label>
          <label>
            <span>{{ t('platform.runningNumbers.fields.strategy') }}</span>
            <p-select
              [options]="strategyOptions()"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="form.strategy"
              (ngModelChange)="refreshPreview()"
              appendTo="body">
            </p-select>
          </label>

          <label>
            <span>{{ t('platform.runningNumbers.fields.prefix') }}</span>
            <input pInputText type="text" [(ngModel)]="form.prefix" (ngModelChange)="refreshPreview()" />
          </label>
          <label>
            <span>{{ t('platform.runningNumbers.fields.dateFormat') }}</span>
            <p-select
              [options]="dateFormatOptions()"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="form.dateFormat"
              (ngModelChange)="refreshPreview()"
              appendTo="body">
            </p-select>
          </label>
          <label>
            <span>{{ t('platform.runningNumbers.fields.separator') }}</span>
            <input pInputText type="text" [(ngModel)]="form.separator" (ngModelChange)="refreshPreview()" />
          </label>
          <label>
            <span>{{ t('platform.runningNumbers.fields.suffix') }}</span>
            <input pInputText type="text" [(ngModel)]="form.suffix" (ngModelChange)="refreshPreview()" />
          </label>

          <label>
            <span>{{ t('platform.runningNumbers.fields.startNumber') }}</span>
            <input pInputText type="number" min="1" [(ngModel)]="form.startNumber" (ngModelChange)="refreshPreview()" />
          </label>
          <label>
            <span>{{ t('platform.runningNumbers.fields.currentNumber') }}</span>
            <input pInputText type="number" min="0" [(ngModel)]="form.currentNumber" (ngModelChange)="refreshPreview()" />
          </label>
          <label>
            <span>{{ t('platform.runningNumbers.fields.incrementBy') }}</span>
            <input pInputText type="number" min="1" [(ngModel)]="form.incrementBy" (ngModelChange)="refreshPreview()" />
          </label>
          <label>
            <span>{{ t('platform.runningNumbers.fields.paddingLength') }}</span>
            <input pInputText type="number" min="1" [(ngModel)]="form.paddingLength" (ngModelChange)="refreshPreview()" />
          </label>

          <label>
            <span>{{ t('platform.runningNumbers.fields.resetFrequency') }}</span>
            <p-select
              [options]="resetFrequencyOptions()"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="form.resetFrequency"
              (ngModelChange)="refreshPreview()"
              appendTo="body">
            </p-select>
          </label>
          <label>
            <span>{{ t('platform.runningNumbers.fields.configuration') }}</span>
            <input pInputText type="text" [(ngModel)]="form.configuration" />
          </label>
          <label class="check">
            <input type="checkbox" [(ngModel)]="form.isActive" />
            <span>{{ t('platform.runningNumbers.fields.isActive') }}</span>
          </label>
          <label>
            <span>{{ t('platform.runningNumbers.fields.preview') }}</span>
            <input pInputText type="text" [ngModel]="previewValue()" disabled />
          </label>
        </div>
        <div class="dialog-actions">
          <button pButton type="button" [label]="t('common.save')" (click)="saveDefinition()"></button>
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="dialogOpen.set(false)"></button>
        </div>
      </p-dialog>

      <p-dialog
        [header]="t('platform.runningNumbers.historyTitle')"
        [visible]="historyDialogOpen()"
        [modal]="true"
        [style]="{ width: '54rem', maxWidth: '96vw' }"
        (visibleChange)="historyDialogOpen.set($event)">
        <p-table [value]="historyRows()">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('platform.runningNumbers.history.generatedNumber') }}</th>
              <th>{{ t('platform.runningNumbers.history.sequenceNumber') }}</th>
              <th>{{ t('platform.runningNumbers.history.entityType') }}</th>
              <th>{{ t('platform.runningNumbers.history.entityId') }}</th>
              <th>{{ t('platform.runningNumbers.history.createdOn') }}</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td>{{ row.generatedNumber }}</td>
              <td>{{ row.sequenceNumber }}</td>
              <td>{{ row.entityType }}</td>
              <td>{{ row.entityId }}</td>
              <td>{{ row.createdAtUtc | date:'yyyy-MM-dd HH:mm' }}</td>
            </tr>
          </ng-template>
        </p-table>
      </p-dialog>

      <p-dialog
        [header]="t('platform.runningNumbers.actions.setCounter')"
        [visible]="counterDialogOpen()"
        [modal]="true"
        [style]="{ width: '24rem', maxWidth: '95vw' }"
        (visibleChange)="onCounterDialogVisibleChange($event)">
        <label class="counter-field">
          <span>{{ t('platform.runningNumbers.messages.enterCounterValue') }}</span>
          <input pInputText type="number" min="0" [(ngModel)]="counterValue" />
        </label>
        <div class="dialog-actions">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="closeCounterDialog()"></button>
          <button pButton type="button" [label]="t('common.save')" (click)="confirmSetCounter()"></button>
        </div>
      </p-dialog>

      <p-dialog
        [header]="t('common.delete')"
        [visible]="deleteDialogOpen()"
        [modal]="true"
        [style]="{ width: '28rem', maxWidth: '95vw' }"
        (visibleChange)="onDeleteDialogVisibleChange($event)">
        <p>{{ t('platform.runningNumbers.messages.confirmDelete') }}</p>
        <div class="dialog-actions">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="closeDeleteDialog()"></button>
          <button pButton type="button" severity="danger" [label]="t('common.delete')" (click)="confirmDeleteDefinition()"></button>
        </div>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .toolbar { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 10px; align-items: center; }
    .entity-filter { min-width: 260px; }
    .actions-cell { display: flex; flex-wrap: wrap; gap: 2px; }
    .message { margin: 8px 0 0; font-size: 13px; }
    .error { color: #b91c1c; }
    .success { color: #166534; }
    .form-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 8px; }
    label { display: grid; gap: 4px; font-size: 12px; color: #334155; }
    input[type='text'], input[type='number'] { height: 32px; border: 1px solid #d1d5db; border-radius: 8px; padding: 0 8px; width: 100%; font-size: 12px; }
    .dialog-actions { margin-top: 10px; display: flex; gap: 8px; justify-content: flex-end; }
    .counter-field { display: grid; gap: 6px; font-size: 13px; color: #334155; }
    .check { display: flex; align-items: center; gap: 8px; }
    @media (max-width: 1100px) { .form-grid { grid-template-columns: 1fr 1fr; } }
    @media (max-width: 700px) { .form-grid { grid-template-columns: 1fr; } }
  `]
})
export class PlatformRunningNumbersComponent implements OnInit {
  private readonly api = inject(PlatformAdminService);
  private readonly financeApi = inject(FinanceApiService);
  private readonly salesApi = inject(SalesApiService);
  private readonly localization = inject(LocalizationService);

  readonly rows = signal<RunningNumberDefinitionRecord[]>([]);
  readonly books = signal<FinancialBook[]>([]);
  readonly documentProfiles = signal<SalesDocumentProfileRecord[]>([]);
  readonly dialogOpen = signal(false);
  readonly historyDialogOpen = signal(false);
  readonly counterDialogOpen = signal(false);
  readonly deleteDialogOpen = signal(false);
  readonly historyRows = signal<RunningNumberHistoryRecord[]>([]);
  readonly selectedDefinitionId = signal<string | null>(null);
  readonly dialogMode = signal<'create' | 'edit'>('create');
  readonly errorMessage = signal('');
  readonly successMessage = signal('');
  readonly previewValue = signal('');
  readonly entityTypeFilterOptions = computed(() => {
    this.localization.resources();
    return [
      { value: '', label: this.t('platform.runningNumbers.allEntityTypes') },
      ...this.entityTypeOptions().map(item => ({ value: item.value, label: item.label }))
    ];
  });
  readonly bookOptions = computed(() =>
    this.books().map(item => ({
      value: item.id,
      label: item.code ? `${item.code} - ${item.name}` : item.name
    })));
  readonly documentProfileOptions = computed(() =>
    this.documentProfiles().map(item => ({
      value: item.id,
      label: item.code ? `${item.code} - ${item.name}` : item.name
    })));

  filterEntityType = '';
  counterValue = 0;
  private pendingSetCounterRow: RunningNumberDefinitionRecord | null = null;
  private pendingDeleteRow: RunningNumberDefinitionRecord | null = null;

  readonly entityTypeOptions = computed(() => {
    this.localization.resources();
    return [
    { value: 'ProcessInstance', label: this.t('platform.runningNumbers.entityTypes.processInstance') },
    { value: 'JournalEntry', label: this.t('platform.runningNumbers.entityTypes.journalEntry') },
    { value: 'PaymentIntent', label: this.t('platform.runningNumbers.entityTypes.paymentIntent') },
    { value: 'WalletTransaction', label: this.t('platform.runningNumbers.entityTypes.walletTransaction') },
    { value: 'Loan', label: this.t('platform.runningNumbers.entityTypes.loan') },
    { value: 'SalesOrder', label: this.t('platform.runningNumbers.entityTypes.salesOrder') },
    { value: 'Receipt', label: this.t('platform.runningNumbers.entityTypes.receipt') },
    { value: 'PaymentRecord', label: this.t('platform.runningNumbers.entityTypes.paymentRecord') },
    { value: 'TransferVoucher', label: this.t('platform.runningNumbers.entityTypes.transferVoucher') },
    { value: 'PosOperation', label: this.t('platform.runningNumbers.entityTypes.posOperation') },
    { value: 'ChequeOperation', label: this.t('platform.runningNumbers.entityTypes.chequeOperation') },
    { value: 'InventoryMovement', label: this.t('platform.runningNumbers.entityTypes.inventoryMovement') },
    { value: 'InventoryCountSession', label: this.t('platform.runningNumbers.entityTypes.inventoryCountSession') },
    { value: 'InventoryLot', label: this.t('platform.runningNumbers.entityTypes.inventoryLot') },
    { value: 'InventorySerial', label: this.t('platform.runningNumbers.entityTypes.inventorySerial') },
    { value: 'CustomerReference', label: this.t('platform.runningNumbers.entityTypes.customerReference') },
    { value: 'Document_Code', label: this.t('platform.runningNumbers.entityTypes.documentCode') },
    { value: 'Document_Reference', label: this.t('platform.runningNumbers.entityTypes.documentReference') },
    { value: 'ReceiptPayment', label: this.t('platform.runningNumbers.entityTypes.receiptPayment') },
    { value: 'Lead', label: this.t('platform.runningNumbers.entityTypes.lead') },
    { value: 'Letter', label: this.t('platform.runningNumbers.entityTypes.letter') },
    { value: 'LetterArchive', label: this.t('platform.runningNumbers.entityTypes.letterArchive') }
    ];
  });

  readonly parentEntityTypeOptions = computed(() => {
    this.localization.resources();
    return [
    { value: '', label: this.t('common.none') },
    { value: 'FinancialBook', label: this.t('platform.runningNumbers.parentTypes.financialBook') },
    { value: 'PaymentsAccounting', label: this.t('platform.runningNumbers.parentTypes.paymentsAccounting') },
    { value: 'ProcessDefinition', label: this.t('platform.runningNumbers.parentTypes.processDefinition') },
    { value: 'SalesDocumentProfile', label: this.t('platform.runningNumbers.parentTypes.salesDocumentProfile') },
    { value: 'Offering', label: this.t('platform.runningNumbers.parentTypes.offering') },
    { value: 'CatalogOperations', label: this.t('platform.runningNumbers.parentTypes.catalogOperations') },
    { value: 'LetterTemplate', label: this.t('platform.runningNumbers.parentTypes.letterTemplate') },
    { value: 'ArchiveFolder', label: this.t('platform.runningNumbers.parentTypes.archiveFolder') }
    ];
  });

  readonly strategyOptions = computed(() => {
    this.localization.resources();
    return [
      { value: 'PerStore', label: this.t('platform.runningNumbers.strategy.perStore') },
      { value: 'Global', label: this.t('platform.runningNumbers.strategy.global') },
      { value: 'StorePrefix', label: this.t('platform.runningNumbers.strategy.storePrefix') }
    ];
  });

  readonly resetFrequencyOptions = computed(() => {
    this.localization.resources();
    return [
      { value: 'Never', label: this.t('platform.runningNumbers.reset.never') },
      { value: 'Daily', label: this.t('platform.runningNumbers.reset.daily') },
      { value: 'Monthly', label: this.t('platform.runningNumbers.reset.monthly') },
      { value: 'Yearly', label: this.t('platform.runningNumbers.reset.yearly') }
    ];
  });

  readonly dateFormatOptions = computed(() => {
    this.localization.resources();
    return [
      { value: '', label: this.t('common.none') },
      { value: 'yyyy', label: 'yyyy' },
      { value: 'yyyyMM', label: 'yyyyMM' },
      { value: 'yyyyMMdd', label: 'yyyyMMdd' }
    ];
  });

  form: SaveRunningNumberDefinitionRequest = this.emptyForm();

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.errorMessage.set('');
    this.api.listRunningNumberDefinitions({ entityType: this.filterEntityType || undefined }).subscribe({
      next: rows => this.rows.set(rows),
      error: () => this.errorMessage.set(this.t('platform.runningNumbers.messages.loadFailed'))
    });
  }

  openCreateDialog(): void {
    this.dialogMode.set('create');
    this.selectedDefinitionId.set(null);
    this.form = this.emptyForm();
    this.dialogOpen.set(true);
    this.refreshPreview();
  }

  onEntityTypeChanged(value: string): void {
    this.form.entityType = value;
    if (value === 'Receipt') {
      this.form.parentEntityType = 'PaymentsAccounting';
      this.form.parentEntityId = 'receipt.received';
    } else if (value === 'PaymentRecord') {
      this.form.parentEntityType = 'PaymentsAccounting';
      this.form.parentEntityId = 'paymentrecord.issued';
    } else if (value === 'SalesOrder') {
      this.form.parentEntityType = 'SalesDocumentProfile';
      this.form.parentEntityId = '';
      this.loadDocumentProfiles();
    } else if (value === 'InventoryLot' || value === 'InventorySerial') {
      this.form.parentEntityType = 'Offering';
      this.form.parentEntityId = '';
    } else if (value === 'CustomerReference') {
      this.form.parentEntityType = 'CatalogOperations';
      this.form.parentEntityId = 'service-order';
    }
    this.refreshPreview();
  }

  onParentEntityTypeChanged(value: string): void {
    const previous = this.form.parentEntityType;
    this.form.parentEntityType = value;
    if (this.form.parentEntityType !== previous) {
      this.form.parentEntityId = '';
    }
    if (this.usesFinancialBookParent()) {
      this.loadBooks();
    }
    if (this.usesDocumentProfileParent()) {
      this.loadDocumentProfiles();
    }
    this.refreshPreview();
  }

  usesFinancialBookParent(): boolean {
    return this.form.parentEntityType === 'FinancialBook';
  }

  usesDocumentProfileParent(): boolean {
    return this.form.parentEntityType === 'SalesDocumentProfile';
  }

  entityTypeLabel(value: string): string {
    this.localization.resources();
    const formValue = this.entityTypeForForm(value);
    const match = this.entityTypeOptions().find(item => item.value === formValue);
    return match?.label ?? value;
  }

  openEditDialog(row: RunningNumberDefinitionRecord): void {
    this.dialogMode.set('edit');
    this.selectedDefinitionId.set(row.id);
    this.form = {
      entityType: this.entityTypeForForm(row.entityType),
      prefix: row.prefix,
      suffix: row.suffix,
      currentNumber: row.currentNumber,
      startNumber: row.startNumber,
      incrementBy: row.incrementBy,
      paddingLength: row.paddingLength,
      dateFormat: row.dateFormat,
      separator: row.separator,
      resetFrequency: row.resetFrequency,
      strategy: row.strategy,
      parentEntityType: row.parentEntityType,
      parentEntityId: row.parentEntityId,
      isActive: row.isActive,
      configuration: row.configuration
    };
    if (this.usesFinancialBookParent()) {
      this.loadBooks();
    }
    if (this.usesDocumentProfileParent()) {
      this.loadDocumentProfiles();
    }
    this.dialogOpen.set(true);
    this.refreshPreview();
  }

  saveDefinition(): void {
    if (!this.form.entityType.trim()) {
      this.errorMessage.set(this.t('platform.runningNumbers.messages.invalidForm'));
      return;
    }

    this.errorMessage.set('');
    this.successMessage.set('');
    const payload = this.normalizedPayload();

    if (this.dialogMode() === 'create') {
      this.api.createRunningNumberDefinition(payload).subscribe({
        next: () => {
          this.successMessage.set(this.t('platform.runningNumbers.messages.created'));
          this.dialogOpen.set(false);
          this.load();
        },
        error: () => this.errorMessage.set(this.t('platform.runningNumbers.messages.saveFailed'))
      });
      return;
    }

    this.api.updateRunningNumberDefinition(this.selectedDefinitionId()!, payload).subscribe({
      next: () => {
        this.successMessage.set(this.t('platform.runningNumbers.messages.saved'));
        this.dialogOpen.set(false);
        this.load();
      },
      error: () => this.errorMessage.set(this.t('platform.runningNumbers.messages.saveFailed'))
    });
  }

  resetCounter(row: RunningNumberDefinitionRecord): void {
    this.api.resetRunningNumberCounter(row.id).subscribe({
      next: () => {
        this.successMessage.set(this.t('platform.runningNumbers.messages.saved'));
        this.load();
      },
      error: () => this.errorMessage.set(this.t('platform.runningNumbers.messages.saveFailed'))
    });
  }

  setCounter(row: RunningNumberDefinitionRecord): void {
    this.pendingSetCounterRow = row;
    this.counterValue = row.currentNumber;
    this.counterDialogOpen.set(true);
  }

  onCounterDialogVisibleChange(visible: boolean): void {
    if (!visible) this.closeCounterDialog();
  }

  closeCounterDialog(): void {
    this.counterDialogOpen.set(false);
    this.pendingSetCounterRow = null;
  }

  confirmSetCounter(): void {
    const row = this.pendingSetCounterRow;
    if (!row) return;

    const numericValue = Number(this.counterValue);
    if (!Number.isInteger(numericValue) || numericValue < 0) {
      this.errorMessage.set(this.t('platform.runningNumbers.messages.invalidCounterValue'));
      return;
    }

    this.api.setRunningNumberCounter(row.id, numericValue).subscribe({
      next: () => {
        this.successMessage.set(this.t('platform.runningNumbers.messages.saved'));
        this.closeCounterDialog();
        this.load();
      },
      error: () => this.errorMessage.set(this.t('platform.runningNumbers.messages.saveFailed'))
    });
  }

  deleteDefinition(row: RunningNumberDefinitionRecord): void {
    this.pendingDeleteRow = row;
    this.deleteDialogOpen.set(true);
  }

  onDeleteDialogVisibleChange(visible: boolean): void {
    if (!visible) this.closeDeleteDialog();
  }

  closeDeleteDialog(): void {
    this.deleteDialogOpen.set(false);
    this.pendingDeleteRow = null;
  }

  confirmDeleteDefinition(): void {
    const row = this.pendingDeleteRow;
    if (!row) return;

    this.api.deleteRunningNumberDefinition(row.id).subscribe({
      next: () => {
        this.successMessage.set(this.t('platform.runningNumbers.messages.deleted'));
        this.closeDeleteDialog();
        this.load();
      },
      error: () => this.errorMessage.set(this.t('platform.runningNumbers.messages.saveFailed'))
    });
  }

  openHistory(row: RunningNumberDefinitionRecord): void {
    this.api.getRunningNumberHistory(row.id).subscribe({
      next: items => {
        this.historyRows.set(items);
        this.historyDialogOpen.set(true);
      },
      error: () => this.errorMessage.set(this.t('platform.runningNumbers.messages.loadFailed'))
    });
  }

  refreshPreview(): void {
    this.api.previewRunningNumber({
      prefix: this.form.prefix,
      suffix: this.form.suffix,
      currentNumber: this.form.currentNumber,
      startNumber: this.form.startNumber,
      incrementBy: this.form.incrementBy,
      paddingLength: this.form.paddingLength,
      dateFormat: this.form.dateFormat,
      separator: this.form.separator,
      strategy: this.form.strategy
    }).subscribe({
      next: result => this.previewValue.set(result.preview),
      error: () => this.previewValue.set('')
    });
  }

  strategyLabel(value: string): string {
    this.localization.resources();
    const key = this.strategyOptions().find(item => item.value.toLowerCase() === value.toLowerCase())?.label;
    return key ?? value;
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private loadBooks(): void {
    if (this.books().length > 0) {
      return;
    }

    this.financeApi.listBooks().subscribe({
      next: rows => this.books.set(rows),
      error: () => this.errorMessage.set(this.t('platform.runningNumbers.messages.booksLoadFailed'))
    });
  }

  private loadDocumentProfiles(): void {
    if (this.documentProfiles().length > 0) {
      return;
    }

    this.salesApi.listDocumentProfiles().subscribe({
      next: rows => {
        const selectedId = this.form.parentEntityId?.trim();
        const selected = selectedId ? rows.find(item => item.id === selectedId) : undefined;
        const active = rows.filter(item => item.isActive);
        if (selected && !active.some(item => item.id === selected.id)) {
          active.push(selected);
        }
        this.documentProfiles.set(active);
      },
      error: () => this.errorMessage.set(this.t('platform.runningNumbers.messages.documentProfilesLoadFailed'))
    });
  }

  /** API persists entity types uppercased (e.g. SALESORDER). */
  private entityTypeForForm(value: string): string {
    if (value.toUpperCase() === 'SALESORDER') {
      return 'SalesOrder';
    }
    return value;
  }

  private normalizedPayload(): SaveRunningNumberDefinitionRequest {
    return {
      entityType: this.form.entityType.trim(),
      prefix: this.form.prefix.trim(),
      suffix: this.form.suffix.trim(),
      currentNumber: Number(this.form.currentNumber || 0),
      startNumber: Number(this.form.startNumber || 1),
      incrementBy: Number(this.form.incrementBy || 1),
      paddingLength: Number(this.form.paddingLength || 5),
      dateFormat: this.form.dateFormat.trim(),
      separator: this.form.separator.trim(),
      resetFrequency: this.form.resetFrequency,
      strategy: this.form.strategy,
      parentEntityType: this.form.parentEntityType.trim(),
      parentEntityId: this.form.parentEntityId.trim(),
      isActive: !!this.form.isActive,
      configuration: this.form.configuration.trim()
    };
  }

  private emptyForm(): SaveRunningNumberDefinitionRequest {
    return {
      entityType: 'ProcessInstance',
      prefix: 'PROC',
      suffix: '',
      currentNumber: 1,
      startNumber: 1,
      incrementBy: 1,
      paddingLength: 5,
      dateFormat: 'yyyy',
      separator: '-',
      resetFrequency: 'Never',
      strategy: 'PerStore',
      parentEntityType: '',
      parentEntityId: '',
      isActive: true,
      configuration: ''
    };
  }
}
